import React, { useMemo, useState } from 'react';
import { Alert, CircularProgress, Snackbar } from '@mui/material';
import {
  AcademicCapIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  FolderOpenIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { Case, Counselor, Mentorship, User } from '../../types';
import { useMentorshipData } from '../../hooks/useMentorshipData';
import { t } from '../../utils/translations';
import ConfirmDialog from '../common/ConfirmDialog';

interface AdminMentorshipPanelProps {
  users: User[];
  counselors: Counselor[];
  allCases: Case[];
  currentUserId: string | undefined;
  currentUserName: string | undefined;
  /** Marking a mentor writes to users/, which this panel does not own. */
  onUsersChanged: () => void | Promise<void>;
}

const selectClass =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-brand-500 focus:outline-none sm:w-auto';

const AdminMentorshipPanel: React.FC<AdminMentorshipPanelProps> = ({
  users,
  counselors,
  allCases,
  currentUserId,
  currentUserName,
  onUsersChanged,
}) => {
  const data = useMentorshipData({
    users,
    counselors,
    allCases,
    currentUserId,
    currentUserName,
  });

  const [mentorToAdd, setMentorToAdd] = useState('');
  const [apprenticePick, setApprenticePick] = useState<Record<string, string>>({});
  const [casesDialog, setCasesDialog] = useState<Mentorship | null>(null);
  const [checkedCaseIds, setCheckedCaseIds] = useState<string[]>([]);
  const [pending, setPending] = useState<
    { type: 'finish' | 'remove'; mentorship: Mentorship } | null
  >(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const fail = (message: string) =>
    setSnackbar({ open: true, message, severity: 'error' });

  const dialogCases = useMemo(
    () => (casesDialog ? data.casesForMentor(casesDialog.mentorUserId) : []),
    [casesDialog, data]
  );

  const handleUnmarkMentor = async (user: User) => {
    const result = await data.unmarkMentor(user.id);
    if (result.ok) {
      await onUsersChanged();
    } else {
      fail(
        result.message
          ? t.admin.mentorship.unmarkBlockedBody.replace('{{names}}', result.message)
          : t.admin.mentorship.actionError
      );
    }
  };

  const handleAddApprentice = async (mentorUserId: string) => {
    const apprenticeUserId = apprenticePick[mentorUserId];
    if (!apprenticeUserId) return;
    const result = await data.addApprentice(mentorUserId, apprenticeUserId);
    if (result.ok) {
      setApprenticePick((prev) => ({ ...prev, [mentorUserId]: '' }));
    } else {
      fail(result.message || t.admin.mentorship.actionError);
    }
  };

  const openCasesDialog = (mentorship: Mentorship) => {
    setCasesDialog(mentorship);
    setCheckedCaseIds(mentorship.caseIds);
  };

  const handleSaveCases = async () => {
    if (!casesDialog) return;
    const result = await data.saveGrantedCases(casesDialog.id, checkedCaseIds);
    if (result.ok) {
      setCasesDialog(null);
    } else {
      fail(result.message || t.admin.mentorship.actionError);
    }
  };

  const handleConfirm = async () => {
    if (!pending) return;
    const result =
      pending.type === 'finish'
        ? await data.finishMentorship(pending.mentorship.id)
        : await data.removeApprentice(pending.mentorship.id);
    if (result.ok) {
      setPending(null);
    } else {
      fail(result.message || t.admin.mentorship.actionError);
    }
  };

  if (data.loading) {
    return (
      <div className="flex justify-center py-12">
        <CircularProgress size={28} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-lg font-semibold text-slate-900">
          {t.admin.mentorship.title}
        </h2>
        <p className="mt-1 text-sm text-slate-500">{t.admin.mentorship.subtitle}</p>
      </div>

      {data.error ? (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {data.error}
        </div>
      ) : null}

      {/* Mark a new mentor */}
      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4">
        <label className="mb-2 block text-sm font-medium text-slate-700">
          {t.admin.mentorship.markMentor}
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <select
            className={selectClass}
            value={mentorToAdd}
            onChange={(event) => setMentorToAdd(event.target.value)}
          >
            <option value="">{t.admin.mentorship.markMentorPlaceholder}</option>
            {data.mentorCandidates.map((user) => (
              <option key={user.id} value={user.id}>
                {user.fullName}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!mentorToAdd || data.busyId === mentorToAdd}
            onClick={async () => {
              const result = await data.markMentor(mentorToAdd);
              if (result.ok) {
                setMentorToAdd('');
                await onUsersChanged();
              } else {
                fail(result.message || t.admin.mentorship.actionError);
              }
            }}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            <AcademicCapIcon className="h-4 w-4" />
            {t.admin.mentorship.markMentor}
          </button>
        </div>
      </div>

      {/* Mentors */}
      {data.mentors.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
          {t.admin.mentorship.mentorsEmpty}
        </div>
      ) : (
        <div className="space-y-4">
          {data.mentors.map((mentor) => {
            const pairings = data.mentorshipsByMentor.get(mentor.id) ?? [];
            const mentorCases = data.casesForMentor(mentor.id);

            return (
              <div
                key={mentor.id}
                className="rounded-xl border border-slate-200 bg-white p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <AcademicCapIcon className="h-5 w-5 text-brand-600" />
                      <span className="font-semibold text-slate-900">
                        {mentor.fullName}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-slate-500">
                      {mentorCases.length} {mentorCases.length === 1 ? 'caz' : 'cazuri'}
                      {' · '}
                      {pairings.filter((p) => p.status === 'active').length}{' '}
                      {t.admin.mentorship.apprenticesTitle.toLowerCase()}
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={data.busyId === mentor.id}
                    onClick={() => void handleUnmarkMentor(mentor)}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {t.admin.mentorship.unmarkMentor}
                  </button>
                </div>

                {/* Apprentices of this mentor */}
                <div className="mt-4 space-y-2">
                  {pairings.length === 0 ? (
                    <p className="text-sm text-slate-400">
                      {t.admin.mentorship.apprenticesEmpty}
                    </p>
                  ) : (
                    pairings.map((pairing) => {
                      const stranded = data.strandedCaseIds(pairing);
                      return (
                        <div
                          key={pairing.id}
                          className="rounded-lg border border-slate-200 bg-slate-50 p-3"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-medium text-slate-800">
                                {pairing.apprenticeName}
                              </span>
                              {pairing.status === 'active' ? (
                                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                                  {t.admin.mentorship.statusActive}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
                                  <CheckCircleIcon className="h-3.5 w-3.5" />
                                  {t.admin.mentorship.statusCompleted}
                                </span>
                              )}
                            </div>

                            <div className="flex flex-wrap gap-2">
                              {pairing.status === 'active' && (
                                <button
                                  type="button"
                                  onClick={() => openCasesDialog(pairing)}
                                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                >
                                  <FolderOpenIcon className="h-4 w-4" />
                                  {t.admin.mentorship.manageCases}
                                </button>
                              )}
                              {pairing.status === 'active' ? (
                                <button
                                  type="button"
                                  disabled={data.busyId === pairing.id}
                                  onClick={() =>
                                    setPending({ type: 'finish', mentorship: pairing })
                                  }
                                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                >
                                  {t.admin.mentorship.finish}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  disabled={data.busyId === pairing.id}
                                  onClick={() => void data.restartMentorship(pairing.id)}
                                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                >
                                  {t.admin.mentorship.reopen}
                                </button>
                              )}
                              <button
                                type="button"
                                disabled={data.busyId === pairing.id}
                                onClick={() =>
                                  setPending({ type: 'remove', mentorship: pairing })
                                }
                                className="rounded-lg border border-red-200 bg-white px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                              >
                                {t.admin.mentorship.remove}
                              </button>
                            </div>
                          </div>

                          {/* Granted cases */}
                          <div className="mt-2">
                            {pairing.caseIds.length === 0 ? (
                              <p className="text-xs text-slate-400">
                                {t.admin.mentorship.casesEmpty}
                              </p>
                            ) : (
                              <ul className="space-y-1">
                                {pairing.caseIds.map((caseId) => (
                                  <li
                                    key={caseId}
                                    className="flex items-start gap-1.5 text-xs text-slate-600"
                                  >
                                    <span className="truncate">
                                      {data.caseTitleById.get(caseId) || caseId}
                                    </span>
                                    {stranded.includes(caseId) && (
                                      <span
                                        title={t.admin.mentorship.strandedWarning}
                                        className="inline-flex shrink-0 items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-amber-700"
                                      >
                                        <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Add apprentice */}
                <div className="mt-3 flex flex-col gap-2 border-t border-slate-100 pt-3 sm:flex-row">
                  <select
                    className={selectClass}
                    value={apprenticePick[mentor.id] || ''}
                    onChange={(event) =>
                      setApprenticePick((prev) => ({
                        ...prev,
                        [mentor.id]: event.target.value,
                      }))
                    }
                  >
                    <option value="">
                      {data.apprenticeCandidates.length === 0
                        ? t.admin.mentorship.noApprenticeCandidates
                        : t.admin.mentorship.addApprenticePlaceholder}
                    </option>
                    {data.apprenticeCandidates.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.fullName}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    disabled={!apprenticePick[mentor.id] || data.busyId === mentor.id}
                    onClick={() => void handleAddApprentice(mentor.id)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <PlusIcon className="h-4 w-4" />
                    {t.admin.mentorship.addApprentice}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Granted-cases dialog */}
      {casesDialog !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl"
          >
            <h3 className="text-base font-semibold text-slate-900">
              {t.admin.mentorship.manageCasesTitle.replace(
                '{{name}}',
                casesDialog.apprenticeName
              )}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              {t.admin.mentorship.manageCasesSubtitle}
            </p>

            <div className="mt-4 max-h-72 space-y-1 overflow-y-auto">
              {dialogCases.length === 0 ? (
                <p className="py-4 text-center text-sm text-slate-400">
                  {t.admin.mentorship.mentorHasNoCases}
                </p>
              ) : (
                dialogCases.map((caseItem) => (
                  <label
                    key={caseItem.id}
                    className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-brand-600"
                      checked={checkedCaseIds.includes(caseItem.id)}
                      onChange={(event) =>
                        setCheckedCaseIds((prev) =>
                          event.target.checked
                            ? [...prev, caseItem.id]
                            : prev.filter((id) => id !== caseItem.id)
                        )
                      }
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-slate-800">
                        {caseItem.title}
                      </span>
                      <span className="block truncate text-xs text-slate-500">
                        {caseItem.counseledName}
                      </span>
                    </span>
                  </label>
                ))
              )}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                disabled={data.busyId === casesDialog.id}
                onClick={() => setCasesDialog(null)}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {t.common.cancel}
              </button>
              <button
                type="button"
                disabled={data.busyId === casesDialog.id}
                onClick={() => void handleSaveCases()}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {t.admin.mentorship.save}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={pending !== null}
        title={
          pending?.type === 'remove'
            ? t.admin.mentorship.removeConfirmTitle
            : t.admin.mentorship.finishConfirmTitle
        }
        message={
          pending?.type === 'remove'
            ? t.admin.mentorship.removeConfirmBody
            : t.admin.mentorship.finishConfirmBody
        }
        variant={pending?.type === 'remove' ? 'danger' : 'default'}
        confirmLabel={
          pending?.type === 'remove'
            ? t.admin.mentorship.remove
            : t.admin.mentorship.finish
        }
        loading={data.busyId === pending?.mentorship.id}
        onClose={() => {
          if (data.busyId !== pending?.mentorship.id) setPending(null);
        }}
        onConfirm={handleConfirm}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          className="rounded-xl"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AdminMentorshipPanel;
